import dayjs from 'dayjs';
import orderBy from 'lodash/orderBy';

import type { DateRange } from '@cast/shared-types';
import {
  WorkloadsGpuUtilizationSummary,
  WorkloadsWastedGpuCostImpactTopItem,
} from '@cast/types';
import {
  divideNumericObject,
  generateForecast,
  sumNumericObjects,
  toBig,
} from '@cast/utils';

import { MOST_EXPENSIVE_ITEM_COLOR_MAP } from '@cast/constants';
import { TimeSeries } from 'types/metrics';
import {
  TopMostExpensiveGroup,
  TopMostExpensiveGroupDatapoint,
} from 'types/top-items';
import { fillGaps } from 'utils/metrics';

import { NormalizedWorkloadGpuUtilizationSummary } from '../_types';

export const normalizeGpuUtilizationSummary = (
  data: WorkloadsGpuUtilizationSummary[]
): NormalizedWorkloadGpuUtilizationSummary[] => {
  return data?.map((item) => {
    return {
      ...item,
      idleMinutes: toBig(item.idleMinutes || 0).toNumber(),
      uptimeMinutes: toBig(item.uptimeMinutes || 0).toNumber(),
      podCount: toBig(item.podCount || 0).toNumber(),
      utilization: item.utilization
        ? {
            gpuPercent: toBig(item.utilization.gpuPercent || 0).toNumber(),
            memoryPercent: toBig(
              item.utilization.memoryPercent || 0
            ).toNumber(),
          }
        : undefined,
      memory: item.memory
        ? {
            availableBytes: toBig(item.memory.availableBytes || 0).toNumber(),
            usedBytes: toBig(item.memory.usedBytes || 0).toNumber(),
            wastedBytes: toBig(item.memory.wastedBytes || 0).toNumber(),
          }
        : undefined,
      costImpact: item.costImpact
        ? {
            onDemand: toBig(item.costImpact.onDemand || 0).toNumber(),
            spot: toBig(item.costImpact.spot || 0).toNumber(),
            spotFallback: toBig(item.costImpact.spotFallback || 0).toNumber(),
            total: toBig(item.costImpact.onDemand || 0)
              .add(toBig(item.costImpact.spot || 0))
              .add(toBig(item.costImpact.spotFallback || 0))
              .toNumber(),
          }
        : undefined,
    };
  });
};

export const workloadsGpuWasteToDatapoints = (
  workloadsGpuWaste: WorkloadsWastedGpuCostImpactTopItem[]
): TimeSeries<{
  isForecast: boolean;
  values: Record<string, number>;
}> => {
  const datapointsByTimestamp: Record<string, Record<string, number>> = {};

  for (const {
    costImpactHistory,
    workloadName,
    workloadType,
    namespace,
  } of workloadsGpuWaste) {
    if (!costImpactHistory || !workloadName || !workloadType || !namespace) {
      continue;
    }
    for (const { timestamp, costImpact } of costImpactHistory) {
      const key = dayjs.utc(timestamp).subtract(1, 'day').toISOString();
      const timestampData = datapointsByTimestamp[key] || {};
      timestampData[`${workloadName}-${workloadType}-${namespace}`] = toBig(
        costImpact || 0
      ).toNumber();
      datapointsByTimestamp[key] = timestampData;
    }
  }

  return orderBy(
    Object.entries(datapointsByTimestamp).map(([timestamp, values]) => ({
      timestamp,
      isForecast: false,
      values,
    })),
    'timestamp'
  );
};

const mapTopWorkloadsGpuWaste = (
  workloadsGpuWaste: WorkloadsWastedGpuCostImpactTopItem[]
): TopMostExpensiveGroup[] => {
  return workloadsGpuWaste.map((item, index) => ({
    id: `${item.workloadName}-${item.workloadType}-${item.namespace}`,
    name: item.workloadName!,
    color: MOST_EXPENSIVE_ITEM_COLOR_MAP.colorByUsageRank[index],
    cost: toBig(item.totalCostImpact || 0).toNumber(),
  }));
};

export const mapTopMostWorkloadsGpuWasteDatapoints = (
  range: DateRange,
  workloadsGpuWaste: WorkloadsWastedGpuCostImpactTopItem[],
  timezone: string
): {
  topWorkloadsGpuWaste: TopMostExpensiveGroup[];
  topWorkloadsGpuWasteDatapoints: TopMostExpensiveGroupDatapoint;
} => {
  if (!workloadsGpuWaste.length) {
    return { topWorkloadsGpuWaste: [], topWorkloadsGpuWasteDatapoints: [] };
  }

  const topWorkloadsGpuWaste = mapTopWorkloadsGpuWaste(workloadsGpuWaste);

  const [from, to] = range;

  const gapsFiller: Record<string, number | null> = topWorkloadsGpuWaste.reduce(
    (acc, { id }) => ({
      ...acc,
      [id]: null,
    }),
    {}
  );

  const datapoints = workloadsGpuWasteToDatapoints(workloadsGpuWaste);

  const unit = 'day';
  const presentDate = dayjs().tz(timezone).startOf(unit);
  const applyForecast = to.isAfter(presentDate, unit);
  const firstFutureTimestamp = presentDate.add(1, unit);

  const dataPointsWithFilledGaps = fillGaps({
    timeSeries: datapoints,
    dateRange: { from, to: applyForecast ? presentDate : to },
    filler: { values: gapsFiller, isForecast: false },
    unit,
  });

  const topWorkloadsGpuWasteDatapoints = applyForecast
    ? generateForecast({
        dateRange: [firstFutureTimestamp, to],
        unit,
        data: dataPointsWithFilledGaps,
        zeroAgg: (timestamp) => ({
          timestamp: timestamp.toISOString(),
          values: gapsFiller,
        }),
        sum: (agg, item) => ({
          timestamp: agg.timestamp,
          values: sumNumericObjects(agg.values, item.values),
        }),
        divide: ({ timestamp, values }, count) => ({
          timestamp,
          isForecast: true,
          values: divideNumericObject(values, count),
        }),
      })
    : dataPointsWithFilledGaps;

  return { topWorkloadsGpuWaste, topWorkloadsGpuWasteDatapoints };
};
